'use client'

import { useState } from 'react'
import { Loader2, MapPinHouse, Pencil, Plus, Trash } from 'lucide-react'
import Modal from '@/components/elements/Modal'
import Skeleton from '@/components/elements/Skeleton'
import EmptyState from '@/components/elements/EmptyState'
import Tabs from '@/components/elements/Tabs'
import Input from '@/components/elements/Input'
import Select from '@/components/elements/Select'
import AddressEdit from './AddressEdit'
import { toast } from '@/lib/toast'
import { useAddressForm, useAddressManagement } from '@/lib/commerce/hooks'
import { formatAddressDetails } from '@/utils/order'
import { getVendorPrimaryColor, resolveColorHex } from '@/utils/util'

const TYPE_LABELS: Record<string, string> = {
  apartment: 'Орон сууц',
  house: 'Хашаа байшин',
  office: 'Оффис',
}

export default function AddressList({ addressList, addressField, vendor }: { addressList: any, addressField: any, vendor: any }) {
  const primaryHex = resolveColorHex(getVendorPrimaryColor(vendor))
  const address: any[] = addressList || []
  const [prevAddressList, setPrevAddressList] = useState<any>(undefined)
  const [prevAddressField, setPrevAddressField] = useState<any>(undefined)
  const [isLoading, setIsLoading] = useState(true)

  if (addressList !== prevAddressList || addressField !== prevAddressField) {
    setPrevAddressList(addressList)
    setPrevAddressField(addressField)
    if (addressList !== undefined && addressField !== undefined) setIsLoading(false)
  }

  const form = useAddressForm({ addressField, vendor })
  const mgmt = useAddressManagement()

  const handleAdd = async () => {
    const v = form.validate()
    if (!v.ok) return toast.error(v.error)
    const ok = await mgmt.addAddress(form.compose())
    if (ok) {
      mgmt.setOpenAdd(false)
      form.reset()
    }
  }

  if (isLoading) return <AddressListSkeleton />

  return (
    <>
      <div className="sm:p-3 space-y-5">
        <section className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-border bg-zinc-50/30">
            <h3 className="font-semibold">Хүргэлтийн хаягууд</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {address.length > 0 ? <>Хадгалагдсан <span className="tabular-nums">{address.length}</span> хаяг</> : 'Хадгалагдсан хаяг байхгүй'}
            </p>
          </div>

          {address.length > 0 ? (
            <div className="divide-y divide-border">
              {address.map((a: any) => {
                const isDefaulting = mgmt.defaultingId === a.id
                const isDeleting = mgmt.deletingId === a.id
                const rowBusy = isDefaulting || isDeleting
                const typeLabel = TYPE_LABELS[a.type || 'apartment'] || 'Хаяг'
                return (
                  <div key={a.id} className="p-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold">{typeLabel}</span>
                        {a.isDefault && (
                          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-md text-white" style={{ background: primaryHex }}>
                            Үндсэн
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 text-sm text-foreground">{`${a?.city || ''}${a?.duureg ? `, ${a.duureg}` : ''}${a?.khoroo ? `, ${a.khoroo}` : ''}`}</div>
                      <div className="text-sm text-muted-foreground">{formatAddressDetails(a)}</div>
                      {a?.additionalAddress && <div className="text-sm text-muted-foreground">{a.additionalAddress}</div>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => !rowBusy && mgmt.startEdit(a)} disabled={rowBusy} className="h-8 w-8 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-accent inline-flex items-center justify-center transition disabled:opacity-50">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => !rowBusy && mgmt.removeAddress(a.id)} disabled={rowBusy} className="h-8 w-8 rounded-md border border-border text-muted-foreground hover:text-rose-600 hover:border-rose-200 inline-flex items-center justify-center transition disabled:opacity-50">
                        {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash className="w-3.5 h-3.5" />}
                      </button>
                      {!a.isDefault && (
                        <button onClick={() => !rowBusy && mgmt.setDefault(a.id)} disabled={rowBusy} className="h-8 px-3 rounded-md border border-border text-xs hover:bg-accent transition disabled:opacity-50 inline-flex items-center gap-1.5">
                          {isDefaulting && <Loader2 className="w-3 h-3 animate-spin" />}
                          Үндсэн болгох
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
              <button onClick={() => mgmt.setOpenAdd(true)} className="px-5 py-3.5 w-full text-sm font-semibold inline-flex items-center justify-center gap-1.5 border-t border-dashed border-border hover:bg-zinc-50/60 transition cursor-pointer">
                <Plus className="w-3.5 h-3.5" style={{ color: primaryHex }} />
                <span style={{ color: primaryHex }}>Шинэ хаяг нэмэх</span>
              </button>
            </div>
          ) : (
            <div className="p-6">
              <EmptyState icon={MapPinHouse} title="Хаяг байхгүй байна" description="Таны нэмсэн шинэ хаягууд энд харагдах болно." />
              <div className="mt-4 flex justify-center">
                <button onClick={() => mgmt.setOpenAdd(true)} className="h-9 px-4 rounded-md text-white text-sm font-medium inline-flex items-center gap-2 hover:opacity-95 transition cursor-pointer" style={{ background: primaryHex }}>
                  <Plus className="w-3.5 h-3.5" />Хаяг нэмэх
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      <Modal open={mgmt.openAdd} setOpen={mgmt.setOpenAdd} saveAction={handleAdd} isSaveActive={!mgmt.submitting} showCancel={false} isLoading={mgmt.submitting} saveText={mgmt.submitting ? 'Хаягийг хадгалж байна...' : 'Хадгалах'} title="Шинэ хаяг оруулах" description="" border={vendor?.border} color={vendor?.color?.primary}>
        <AddressFormFields form={form} vendor={vendor} />
      </Modal>

      <AddressEdit open={mgmt.openEdit} setOpen={mgmt.setOpenEdit} address={mgmt.addressToEdit} addressField={addressField} vendor={vendor} updateAddress={mgmt.updateAddress} submitting={mgmt.submitting} />
    </>
  )
}

export function AddressFormFields({ form, vendor }: { form: ReturnType<typeof useAddressForm>, vendor: any }) {
  return (
    <div className="flex flex-col gap-4">
      <Tabs tabs={[{ label: 'Орон сууц', value: 'apartment', content: null }, { label: 'Хашаа байшин', value: 'house', content: null }, { label: 'Оффис', value: 'office', content: null }]} defaultValue={form.addressType} onValueChange={(value: string) => form.setAddressType(value as any)} className="w-full" />
      <Select label="Хот / Аймаг" items={form.provinces} value={form.selectedProvince || ''} onChange={form.onProvinceChange} required />
      <Select label="Дүүрэг / Сум" value={form.selectedDistrict || ''} items={form.districts} required onChange={form.onDistrictChange} />
      <Select label="Хороо / Баг" value={form.selectedSubdistrict || ''} items={form.subdistricts} required onChange={form.onSubdistrictChange} />
      {form.addressType === 'apartment' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Байр" placeholder="Байрны дугаарыг оруулна уу" value={form.bair} onChange={(e: any) => form.setBair(e.target.value)} required color={vendor?.color?.primary} />
          <Input label="Тоот" placeholder="Хаалганы тоотоо оруулна уу" value={form.toot} onChange={(e: any) => form.setToot(e.target.value)} required color={vendor?.color?.primary} />
        </div>
      )}
      {form.addressType === 'house' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Хашааны дугаар" placeholder="Хашааны дугаар оруулна уу" value={form.houseNum} onChange={(e: any) => form.setHouseNum(e.target.value)} required color={vendor?.color?.primary} />
          <Input label="Гудамж" placeholder="Гудамжны нэр" value={form.street} onChange={(e: any) => form.setStreet(e.target.value)} color={vendor?.color?.primary} />
        </div>
      )}
      {form.addressType === 'office' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Барилга" placeholder="Барилгын нэр/дугаар" value={form.building} onChange={(e: any) => form.setBuilding(e.target.value)} required color={vendor?.color?.primary} />
            <Input label="Давхар" placeholder="Давхар" value={form.floor} onChange={(e: any) => form.setFloor(e.target.value)} required color={vendor?.color?.primary} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Тоот" placeholder="Хаалганы тоотоо оруулна уу" value={form.toot} onChange={(e: any) => form.setToot(e.target.value)} required color={vendor?.color?.primary} />
            <Input label="Оффисын нэр" placeholder="Оффисын нэр (заавал биш)" value={form.officeName} onChange={(e: any) => form.setOfficeName(e.target.value)} color={vendor?.color?.primary} />
          </div>
        </>
      )}
      <Input label="Нэмэлт мэдээлэл" placeholder="Нэмэлт мэдээлэл оруулна уу" value={form.additionalAddress} onChange={(e: any) => form.setAdditionalAddress(e.target.value)} description="Орц, давхар, орцны код гэх мэт" color={vendor?.color?.primary} />
    </div>
  )
}

function AddressListSkeleton() {
  return (
    <div className="sm:p-3 space-y-5">
      <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-border bg-zinc-50/30">
          <Skeleton className="w-40 h-5" />
          <Skeleton className="w-32 h-4 mt-1.5" />
        </div>
        <div className="divide-y divide-border">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="p-5 grid grid-cols-[1fr_auto] gap-3 items-start">
              <div className="flex flex-col gap-1.5">
                <Skeleton className="w-24 h-5" />
                <Skeleton className="w-72 h-4" />
                <Skeleton className="w-56 h-4" />
                <Skeleton className="w-32 h-4" />
              </div>
              <div className="flex items-center gap-1.5">
                <Skeleton className="w-8 h-8 rounded-md" />
                <Skeleton className="w-8 h-8 rounded-md" />
                <Skeleton className="w-24 h-8 rounded-md" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
